import { useDescope } from '@descope/react-sdk/flows';
import { useCallback, useEffect, useRef, useState } from 'react';
import { addClientTiming } from '@shared/utils/clientMonitoring';
import { reportClientError } from '@shared/utils/reportClientError';
import { browserSupportsConditionalMediation } from './passkeys';

export const CONDITIONAL_PASSKEY_CAPABLE_TIMING =
  'passkey_conditional_mediation_supported';
export const CONDITIONAL_PASSKEY_UNSUPPORTED_TIMING =
  'passkey_conditional_mediation_unsupported';
export const CONDITIONAL_PASSKEY_STARTED_TIMING =
  'passkey_conditional_mediation_started';
export const CONDITIONAL_PASSKEY_ABORTED_TIMING =
  'passkey_conditional_mediation_aborted';
export const CONDITIONAL_PASSKEY_REJECTED_TIMING =
  'passkey_conditional_mediation_rejected';
export const CONDITIONAL_PASSKEY_SUBMISSION_TIMEOUT_MS = 15_000;

type ConditionalPasskeyState =
  'idle' | 'starting' | 'pending' | 'submitting' | 'cancelled';

type ConditionalPasskeyEndReason =
  | 'none'
  | 'competing-method'
  | 'gate-disabled'
  | 'unsupported'
  | 'start-failed'
  | 'assertion-ended'
  | 'submission-settled'
  | 'submission-failed';

interface ConditionalPasskeyPayload {
  transactionId: string;
  response: string;
}

interface UseConditionalPasskeyLoginOptions {
  enabled: boolean;
  onAssertionResolved?: (payload: ConditionalPasskeyPayload) => Promise<void>;
  onResolvedAssertionFailure: () => void;
  submissionTimeoutMs?: number;
}

interface ConditionalPasskeyLoginController {
  /** Returns false once a resolved assertion has committed to submission. */
  continueWithCompetingMethod: () => boolean;
  hasTimedOut: boolean;
  isSubmitting: boolean;
  restartAfterCompetingMethodFailure: () => void;
}

const getErrorName = (error: unknown): string | undefined => {
  if (error instanceof DOMException || error instanceof Error) {
    return error.name;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    return error.name;
  }

  return undefined;
};

export const isExpectedConditionalPasskeyExit = (error: unknown): boolean => {
  const errorName = getErrorName(error)?.toLowerCase();
  if (errorName === 'aborterror' || errorName === 'notallowederror') {
    return true;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'reason' in error &&
    typeof error.reason === 'string'
  ) {
    const reason = error.reason.toLowerCase();
    return reason === 'aborted' || reason === 'not_allowed';
  }

  return false;
};

export const useConditionalPasskeyLogin = ({
  enabled,
  onAssertionResolved,
  onResolvedAssertionFailure,
  submissionTimeoutMs = CONDITIONAL_PASSKEY_SUBMISSION_TIMEOUT_MS,
}: UseConditionalPasskeyLoginOptions): ConditionalPasskeyLoginController => {
  const sdk = useDescope();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [restartToken, setRestartToken] = useState(0);
  const stateRef = useRef<ConditionalPasskeyState>('idle');
  const endReasonRef = useRef<ConditionalPasskeyEndReason>('none');
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const submissionTimeoutRef = useRef<number | undefined>(undefined);
  const runIdRef = useRef(0);
  const hasAttemptedRef = useRef(false);
  const hasRetriedAssertionSubmissionRef = useRef(false);
  const hasRetriedGateFlapRef = useRef(false);
  const restartRequestedRef = useRef(false);
  const enabledRef = useRef(enabled);
  const onAssertionResolvedRef = useRef(onAssertionResolved);
  const onResolvedAssertionFailureRef = useRef(onResolvedAssertionFailure);

  enabledRef.current = enabled;
  onAssertionResolvedRef.current = onAssertionResolved;
  onResolvedAssertionFailureRef.current = onResolvedAssertionFailure;

  const cancelActiveRequest = useCallback(
    (reason: 'competing-method' | 'gate-disabled'): boolean => {
      if (stateRef.current === 'submitting') {
        return false;
      }

      if (stateRef.current === 'starting' || stateRef.current === 'pending') {
        runIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = undefined;
        stateRef.current = 'cancelled';
        endReasonRef.current = reason;
        addClientTiming(CONDITIONAL_PASSKEY_ABORTED_TIMING);
      }

      return true;
    },
    []
  );

  const cancelPendingRequest = useCallback((): boolean => {
    return cancelActiveRequest('competing-method');
  }, [cancelActiveRequest]);

  const restartConditionalRequest = useCallback((): void => {
    restartRequestedRef.current = false;
    hasAttemptedRef.current = false;
    stateRef.current = 'idle';
    endReasonRef.current = 'none';
    setRestartToken(value => value + 1);
  }, []);

  const restartAfterAssertionSubmissionFailure = useCallback((): void => {
    restartRequestedRef.current = false;
    if (!enabledRef.current || hasRetriedAssertionSubmissionRef.current) {
      return;
    }

    hasRetriedAssertionSubmissionRef.current = true;
    restartConditionalRequest();
  }, [restartConditionalRequest]);

  const restartAfterCompetingMethodFailure = useCallback((): void => {
    if (!enabledRef.current) {
      return;
    }

    if (stateRef.current === 'submitting') {
      restartRequestedRef.current = true;
      return;
    }

    if (
      stateRef.current === 'cancelled' &&
      endReasonRef.current === 'competing-method'
    ) {
      restartConditionalRequest();
    } else if (
      stateRef.current === 'cancelled' &&
      endReasonRef.current === 'submission-settled'
    ) {
      restartAfterAssertionSubmissionFailure();
    }
  }, [restartAfterAssertionSubmissionFailure, restartConditionalRequest]);

  useEffect(() => {
    if (!enabled) {
      const cancelledActiveRequest =
        stateRef.current === 'starting' || stateRef.current === 'pending';
      restartRequestedRef.current = false;
      cancelActiveRequest('gate-disabled');
      if (cancelledActiveRequest && !hasRetriedGateFlapRef.current) {
        hasRetriedGateFlapRef.current = true;
        hasAttemptedRef.current = false;
      }
      return;
    }

    if (hasAttemptedRef.current || !onAssertionResolvedRef.current) {
      return;
    }

    hasAttemptedRef.current = true;
    stateRef.current = 'starting';
    endReasonRef.current = 'none';
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;

    const isCurrentRun = (): boolean => runIdRef.current === runId;

    const rejectSafely = (reason: string): void => {
      if (!isCurrentRun()) {
        return;
      }

      stateRef.current = 'cancelled';
      endReasonRef.current = 'start-failed';
      addClientTiming(CONDITIONAL_PASSKEY_REJECTED_TIMING);
      reportClientError(
        'Conditional passkey login failed',
        new Error('Conditional passkey login failed'),
        {
          flow: 'universal-login',
          reason,
        }
      );
    };

    const startConditionalPasskeyLogin = async (): Promise<void> => {
      const supportsConditionalMediation =
        await browserSupportsConditionalMediation();
      if (!isCurrentRun()) {
        return;
      }

      addClientTiming(
        supportsConditionalMediation
          ? CONDITIONAL_PASSKEY_CAPABLE_TIMING
          : CONDITIONAL_PASSKEY_UNSUPPORTED_TIMING
      );
      if (!supportsConditionalMediation) {
        stateRef.current = 'cancelled';
        endReasonRef.current = 'unsupported';
        return;
      }

      const startResult = await sdk.webauthn.signIn.start(
        '',
        window.location.origin
      );
      if (!isCurrentRun()) {
        return;
      }

      const transactionId = startResult.data?.transactionId;
      const options = startResult.data?.options;
      if (!startResult.ok || !transactionId || !options) {
        rejectSafely('conditional-passkey-start-failed');
        return;
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      stateRef.current = 'pending';
      addClientTiming(CONDITIONAL_PASSKEY_STARTED_TIMING);

      let response: string;
      try {
        response = await sdk.webauthn.helpers.conditional(
          options,
          abortController
        );
      } catch (error) {
        if (!isCurrentRun()) {
          return;
        }

        abortControllerRef.current = undefined;
        stateRef.current = 'cancelled';
        endReasonRef.current = 'assertion-ended';
        addClientTiming(CONDITIONAL_PASSKEY_REJECTED_TIMING);
        if (!isExpectedConditionalPasskeyExit(error)) {
          reportClientError(
            'Conditional passkey assertion failed',
            new Error('Conditional passkey assertion failed'),
            {
              flow: 'universal-login',
              reason: 'conditional-passkey-assertion-failed',
            }
          );
        }
        return;
      }

      if (!isCurrentRun() || abortController.signal.aborted) {
        return;
      }

      stateRef.current = 'submitting';
      abortControllerRef.current = undefined;
      setIsSubmitting(true);
      try {
        const submission = onAssertionResolvedRef.current?.({
          transactionId,
          response,
        });
        submissionTimeoutRef.current = window.setTimeout(() => {
          if (!isCurrentRun() || stateRef.current !== 'submitting') {
            return;
          }

          reportClientError(
            'Conditional passkey submission timed out',
            new Error('Conditional passkey submission timed out'),
            {
              flow: 'universal-login',
              reason: 'conditional-passkey-submission-timeout',
            }
          );
          setHasTimedOut(true);
        }, submissionTimeoutMs);
        await submission;
        if (isCurrentRun()) {
          // A resolved flow action can legitimately leave this mounted login
          // screen in place. Restore fallbacks once it settles rather than
          // treating the submission as a permanent terminal state.
          stateRef.current = 'cancelled';
          endReasonRef.current = 'submission-settled';
          setHasTimedOut(false);
          setIsSubmitting(false);
          if (restartRequestedRef.current) {
            restartAfterAssertionSubmissionFailure();
          }
        }
      } catch {
        if (!isCurrentRun()) {
          return;
        }

        stateRef.current = 'cancelled';
        endReasonRef.current = 'submission-failed';
        restartRequestedRef.current = false;
        setHasTimedOut(false);
        setIsSubmitting(false);
        addClientTiming(CONDITIONAL_PASSKEY_REJECTED_TIMING);
        reportClientError(
          'Conditional passkey submission failed',
          new Error('Conditional passkey submission failed'),
          {
            flow: 'universal-login',
            reason: 'conditional-passkey-submission-failed',
          }
        );
        onResolvedAssertionFailureRef.current();
        restartAfterAssertionSubmissionFailure();
      } finally {
        if (submissionTimeoutRef.current !== undefined) {
          window.clearTimeout(submissionTimeoutRef.current);
          submissionTimeoutRef.current = undefined;
        }
      }
    };

    void startConditionalPasskeyLogin().catch(() => {
      rejectSafely('conditional-passkey-start-failed');
    });
  }, [
    cancelActiveRequest,
    enabled,
    restartAfterAssertionSubmissionFailure,
    restartConditionalRequest,
    restartToken,
    sdk,
    submissionTimeoutMs,
  ]);

  useEffect(() => {
    return () => {
      if (stateRef.current === 'starting' || stateRef.current === 'pending') {
        addClientTiming(CONDITIONAL_PASSKEY_ABORTED_TIMING);
      }
      runIdRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = undefined;
      if (submissionTimeoutRef.current !== undefined) {
        window.clearTimeout(submissionTimeoutRef.current);
        submissionTimeoutRef.current = undefined;
      }
      // React StrictMode replays effects in development. Reset the attempt
      // marker so the replay owns the one real request after invalidating the
      // abandoned run; on a genuine unmount there is no subsequent render.
      hasAttemptedRef.current = false;
      hasRetriedAssertionSubmissionRef.current = false;
      hasRetriedGateFlapRef.current = false;
      restartRequestedRef.current = false;
      stateRef.current = 'idle';
      endReasonRef.current = 'none';
    };
  }, []);

  return {
    continueWithCompetingMethod: cancelPendingRequest,
    hasTimedOut,
    isSubmitting,
    restartAfterCompetingMethodFailure,
  };
};
